const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    this.fallbackProductsCache = null;

    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    });

    const searchInput = this.querySelector('.main-search__input');

    if (searchInput) {
      searchInput.addEventListener('keyup', (evt) => {
        if (evt.key === 'Escape') {
          const closeBtn = this.querySelector('.main-search__close');
          if (closeBtn) closeBtn.dispatchEvent(new Event('click'));
        }
      });
    }

    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    if (!searchInput) return;

    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';

    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      if (!resultsBox) return;

      const valueToSearch = searchInput.value.trim();

      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        this.oldSearchValue = valueToSearch;

        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        clearTimeout(searchTimeoutID);

        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => {
          linkURL.searchParams.set(key, value);
        });

        this.classList.remove(
          'main-search--has-results',
          'main-search--results-on-multiple-lines',
          'main-search--no-results'
        );

        this.classList.add('main-search--loading');

        resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';

        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();

          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set(
              'resources[options][fields]',
              includeMeta
                ? 'title,product_type,variants.title,vendor,tag,variants.sku'
                : 'title,product_type,variants.title,vendor'
            );
          } else {
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }

              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;

              let resultsList = null;

              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
              }

              searchAbortController = null;

              this.classList.remove(
                'main-search--has-results',
                'main-search--results-on-multiple-lines',
                'main-search--no-results',
                'main-search--loading'
              );

              resultsBox.innerHTML = '';

              if (!resultsList) {
                this.renderClosestFallback(resultsBox, valueToSearch, linkURL);
                return;
              }

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';

              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                if (index < resultLimit) {
                  block.classList.add('main-search-result');

                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));

                  resultsProducts.firstElementChild.appendChild(block);
                }
              });

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const firstLink = block.querySelector('a');
                const title = block.querySelector('.page-block__title');

                if (!firstLink || !title) return;

                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = firstLink.href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = title.innerText;

                resultsPages.appendChild(item);
              });

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');

              if (areProducts || arePages) {
                this.classList.add('main-search--has-results');
                this.classList.toggle(
                  'main-search--results-on-multiple-lines',
                  resultsProducts.querySelectorAll('.product-block').length > 4
                );

                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }

                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;

                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;

                resultsBox.appendChild(allLink);
              } else {
                this.renderClosestFallback(resultsBox, valueToSearch, linkURL);
              }
            })
            .catch((error) => {
              if (error && error.name === 'AbortError') return;
              console.warn(error);

              this.classList.remove('main-search--loading');
              this.renderClosestFallback(resultsBox, valueToSearch, linkURL);
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        this.oldSearchValue = valueToSearch;

        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        clearTimeout(searchTimeoutID);

        this.classList.remove(
          'main-search--has-results',
          'main-search--results-on-multiple-lines',
          'main-search--loading',
          'main-search--no-results'
        );

        resultsBox.innerHTML = '';
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }

  buildStoreUrl(path) {
    const base = theme && theme.routes && theme.routes.base ? theme.routes.base : '/';
    const cleanBase = base.endsWith('/') ? base : `${base}/`;
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;

    return `${cleanBase}${cleanPath}`;
  }

  normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  reduceRepeatedLetters(value) {
    return String(value || '').replace(/(.)\1{2,}/g, '$1$1');
  }

  levenshtein(a, b) {
    a = this.normalizeText(a);
    b = this.normalizeText(b);

    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    const matrix = [];

    for (let i = 0; i <= b.length; i += 1) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i += 1) {
      for (let j = 1; j <= a.length; j += 1) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }

  scoreProduct(product, query) {
    const rawQuery = this.normalizeText(query);
    const softQuery = this.reduceRepeatedLetters(rawQuery);

    const title = this.normalizeText(product.title);
    const handle = this.normalizeText(product.handle);
    const type = this.normalizeText(product.product_type);
    const vendor = this.normalizeText(product.vendor);
    const tags = Array.isArray(product.tags) ? this.normalizeText(product.tags.join(' ')) : '';

    const haystack = `${title} ${handle} ${type} ${vendor} ${tags}`;
    const queryTokens = rawQuery.split(' ').filter(Boolean);
    const softTokens = softQuery.split(' ').filter(Boolean);

    let score = 0;

    if (title === rawQuery || title === softQuery) score += 200;
    if (title.includes(rawQuery)) score += 120;
    if (title.includes(softQuery)) score += 110;
    if (handle.includes(rawQuery)) score += 95;
    if (handle.includes(softQuery)) score += 85;
    if (haystack.includes(rawQuery)) score += 70;
    if (haystack.includes(softQuery)) score += 60;

    queryTokens.forEach((token) => {
      if (token.length < 2) return;

      if (title.includes(token)) score += 28;
      if (handle.includes(token)) score += 22;
      if (tags.includes(token)) score += 18;
      if (type.includes(token)) score += 12;
    });

    softTokens.forEach((token) => {
      if (token.length < 2) return;

      if (title.includes(token)) score += 24;
      if (handle.includes(token)) score += 20;
      if (tags.includes(token)) score += 15;
    });

    const titleWords = title.split(' ').filter((word) => word.length > 2);
    const queryWords = softTokens.filter((word) => word.length > 2);

    queryWords.forEach((queryWord) => {
      let bestDistance = 99;

      titleWords.forEach((titleWord) => {
        const distance = this.levenshtein(queryWord, titleWord);
        if (distance < bestDistance) bestDistance = distance;
      });

      if (bestDistance === 0) score += 25;
      else if (bestDistance === 1) score += 18;
      else if (bestDistance === 2) score += 10;
    });

    if (product.available === false) score -= 25;

    return score;
  }

  getProductImage(product) {
    if (product.featured_image) return product.featured_image;
    if (product.image && product.image.src) return product.image.src;
    if (product.images && product.images.length) {
      if (typeof product.images[0] === 'string') return product.images[0];
      if (product.images[0].src) return product.images[0].src;
    }

    return '';
  }

  getProductPrice(product) {
    if (!product.variants || !product.variants.length) return '';

    const prices = product.variants
      .map((variant) => Number(variant.price))
      .filter((price) => !Number.isNaN(price));

    if (!prices.length) return '';

    const lowest = Math.min.apply(null, prices);

    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(lowest, theme.moneyFormat || '${{amount}}');
    }

    return '';
  }

  fetchFallbackProducts() {
    if (this.fallbackProductsCache) {
      return Promise.resolve(this.fallbackProductsCache);
    }

    const sourceUrl = this.buildStoreUrl('collections/all-sports-wall-art/products.json?limit=250');

    return fetch(sourceUrl, { method: 'get' })
      .then((response) => {
        if (!response.ok) throw new Error('Fallback products unavailable');
        return response.json();
      })
      .then((data) => {
        const products = data && data.products ? data.products : [];
        this.fallbackProductsCache = products;
        return products;
      });
  }

  async renderClosestFallback(resultsBox, query, linkURL) {
    this.injectFallbackStyles();

    this.classList.remove('main-search--loading', 'main-search--has-results');
    this.classList.add('main-search--no-results');

    resultsBox.innerHTML = `
      <div class="sc-search-fallback">
        <div class="sc-search-fallback__eyebrow">No exact match found</div>
        <div class="sc-search-fallback__title">Searching closest editions…</div>
      </div>
    `;

    try {
      const products = await this.fetchFallbackProducts();

      const ranked = products
        .map((product) => ({
          product,
          score: this.scoreProduct(product, query)
        }))
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 4)
        .map((item) => item.product);

      const fallbackProducts = ranked.length ? ranked : products.slice(0, 4);

      const productCards = fallbackProducts.map((product) => {
        const image = this.getProductImage(product);
        const price = this.getProductPrice(product);
        const url = this.buildStoreUrl(`products/${product.handle}`);

        return `
          <a class="sc-search-fallback-card" href="${url}">
            <span class="sc-search-fallback-card__image">
              ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
            </span>
            <span class="sc-search-fallback-card__title">${this.escapeHtml(product.title)}</span>
            ${price ? `<span class="sc-search-fallback-card__price">${price}</span>` : ''}
          </a>
        `;
      }).join('');

      resultsBox.innerHTML = `
        <div class="sc-search-fallback">
          <div class="sc-search-fallback__eyebrow">No exact match found</div>
          <div class="sc-search-fallback__title">Closest available editions</div>
          <p class="sc-search-fallback__copy">
            Couldn’t find an exact match for “${this.escapeHtml(query)}”. These are the closest editions we found.
          </p>

          <div class="sc-search-fallback-grid">
            ${productCards}
          </div>

          <div class="sc-search-fallback-request">
            <strong>Can’t find the design you want?</strong>
            <span>Send us a message with your request.</span>
            <a href="${this.buildStoreUrl('pages/contact')}" class="sc-search-fallback-request__btn">
              Request a Design
            </a>
          </div>

          <a class="main-search__results-all-link btn btn--secondary" href="${linkURL}">
            View all search results
          </a>
        </div>
      `;
    } catch (error) {
      console.warn(error);

      resultsBox.innerHTML = `
        <div class="sc-search-fallback">
          <div class="sc-search-fallback__eyebrow">No exact match found</div>
          <div class="sc-search-fallback__title">Can’t find the design?</div>
          <p class="sc-search-fallback__copy">
            Send us a message with your request and we’ll help you find the right edition.
          </p>

          <div class="sc-search-fallback-request">
            <a href="${this.buildStoreUrl('pages/contact')}" class="sc-search-fallback-request__btn">
              Request a Design
            </a>
          </div>
        </div>
      `;
    }
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  injectFallbackStyles() {
    if (document.getElementById('sc-search-fallback-styles')) return;

    const style = document.createElement('style');
    style.id = 'sc-search-fallback-styles';
    style.textContent = `
      .sc-search-fallback{
        width:100%;
        max-width:980px;
        margin:0 auto;
        text-align:center;
        color:#F5F2EA;
      }

      .sc-search-fallback__eyebrow{
        margin:0 0 8px;
        color:#D4A54C;
        font-size:11px;
        font-weight:850;
        letter-spacing:.18em;
        text-transform:uppercase;
      }

      .sc-search-fallback__title{
        color:#F5F2EA;
        font-size:clamp(22px,3vw,32px);
        line-height:1.1;
        font-weight:900;
        letter-spacing:.035em;
        text-transform:uppercase;
      }

      .sc-search-fallback__copy{
        max-width:560px;
        margin:12px auto 0;
        color:rgba(245,242,234,.70);
        font-size:14px;
        line-height:1.5;
      }

      .sc-search-fallback-grid{
        display:grid;
        grid-template-columns:repeat(4,minmax(0,1fr));
        gap:18px;
        margin:24px auto 0;
      }

      .sc-search-fallback-card{
        display:block;
        color:#F5F2EA;
        text-decoration:none;
        text-align:center;
      }

      .sc-search-fallback-card__image{
        display:block;
        overflow:hidden;
        border-radius:14px;
        border:1px solid rgba(212,165,76,.28);
        background:#050505;
        box-shadow:0 14px 30px rgba(0,0,0,.22);
        aspect-ratio:1 / 1;
      }

      .sc-search-fallback-card__image img{
        width:100%;
        height:100%;
        display:block;
        object-fit:cover;
      }

      .sc-search-fallback-card__title{
        display:block;
        margin:10px auto 0;
        color:#F5F2EA;
        font-size:13px;
        font-weight:800;
        line-height:1.25;
      }

      .sc-search-fallback-card__price{
        display:block;
        margin:4px auto 0;
        color:#D4A54C;
        font-size:12px;
        font-weight:850;
      }

      .sc-search-fallback-request{
        max-width:560px;
        margin:24px auto 0;
        padding:16px 18px;
        border-radius:16px;
        border:1px solid rgba(212,165,76,.28);
        background:rgba(255,255,255,.04);
        color:#F5F2EA;
        display:flex;
        flex-direction:column;
        align-items:center;
        gap:6px;
      }

      .sc-search-fallback-request strong{
        font-size:14px;
        font-weight:900;
      }

      .sc-search-fallback-request span{
        color:rgba(245,242,234,.68);
        font-size:13px;
      }

      .sc-search-fallback-request__btn{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        margin-top:6px;
        min-height:40px;
        padding:10px 18px;
        border-radius:999px;
        border:1px solid rgba(212,165,76,.65);
        background:linear-gradient(180deg, rgba(212,165,76,.28) 0%, rgba(212,165,76,.14) 100%);
        color:#F5F2EA;
        text-decoration:none;
        font-size:11px;
        font-weight:900;
        letter-spacing:.14em;
        text-transform:uppercase;
      }

      .sc-search-fallback .main-search__results-all-link{
        margin-top:18px;
      }

      @media (max-width:749px){
        .sc-search-fallback-grid{
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:14px;
        }

        .sc-search-fallback-card__title{
          font-size:12px;
        }
      }
    `;

    document.head.appendChild(style);
  }
};

if (!window.customElements.get('main-search')) {
  window.customElements.define('main-search', MainSearch);
}